class GrassAPI {
  constructor({ axios, activeFarm }) {
    this.axios = axios;
    this.activeFarm = activeFarm;
    this.eventListeners = {};
  }

  setAccessToken = (token) => {
    if (!this.axios) return;
    if (token) {
      this.axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete this.axios.defaults.headers.common.Authorization;
    }
  };

  addEventListener = (event, listener) => {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(listener);
  };

  removeEventListener = (event, listener) => {
    if (this.eventListeners[event]) {
      this.eventListeners[event] = this.eventListeners[event].filter(
        (l) => l !== listener
      );
    }
  };

  dispatchEvent = (event, data) => {
    if (this.eventListeners[event]) {
      this.eventListeners[event].forEach((listener) => listener(data));
    }
  };

  getEventCoverMeasures = (filter = {}) =>
    getEventCoverMeasures(this.axios, filter);
  getEventCoverMeasureChart = (filter = {}) =>
    getEventCoverMeasureChart(this.axios, filter);
  getEventCoverMeasure = (id) => getEventCoverMeasure(this.axios, id);
  postEventCoverMeasure = (obj) => postEventCoverMeasure(this.axios, obj);
  patchEventCoverMeasure = (id, obj) =>
    patchEventCoverMeasure(this.axios, id, obj);
  deleteEventCoverMeasure = (id) => deleteEventCoverMeasure(this.axios, id);

  getEventFertilisers = (filter = {}) =>
    getEventFertilisers(this.axios, filter);
  getEventFertiliserChart = (filter = {}) =>
    getEventFertiliserChart(this.axios, filter);
  getEventFertiliser = (id) => getEventFertiliser(this.axios, id);
  postEventFertiliser = (obj) => postEventFertiliser(this.axios, obj);
  patchEventFertiliser = (id, obj) => patchEventFertiliser(this.axios, id, obj);
  deleteEventFertiliser = (id) => deleteEventFertiliser(this.axios, id);

  getEventGrazings = (filter = {}) => getEventGrazings(this.axios, filter);
  getEventGrazing = (id) => getEventGrazing(this.axios, id);
  postEventGrazing = (obj) => postEventGrazing(this.axios, obj);
  patchEventGrazing = (id, obj) => patchEventGrazing(this.axios, id, obj);
  deleteEventGrazing = (id) => deleteEventGrazing(this.axios, id);

  getEventCuts = (filter = {}) => getEventCuts(this.axios, filter);
  getEventCut = (id) => getEventCut(this.axios, id);
  postEventCut = (obj) => postEventCut(this.axios, obj);
  patchEventCut = (id, obj) => patchEventCut(this.axios, id, obj);
  deleteEventCut = (id) => deleteEventCut(this.axios, id);

  getEventReseedings = (filter = {}) => getEventReseedings(this.axios, filter);
  getEventReseeding = (id) => getEventReseeding(this.axios, id);
  postEventReseeding = (obj) => postEventReseeding(this.axios, obj);
  patchEventReseeding = (id, obj) => patchEventReseeding(this.axios, id, obj);
  deleteEventReseeding = (id) => deleteEventReseeding(this.axios, id);

  postEventCoverMeasuure = (obj) => postEventCoverMeasuure(this.axios, obj);

  getWpClass = (date) => getWpClass(this.axios, date);
  getMapConfig = () => getMapConfig(this.axios);

  getFarms = () => getFarms(this.axios);
  getFarmCoverSummary = (farmId) => getFarmCoverSummary(this.axios, farmId);
  /** Fetch cover-band totals and unmeasured area for an accessible farm UUID. */
  getFarmCoverDistribution = (farmId) =>
    getFarmCoverDistribution(this.axios, farmId);
  getFarmCoverFields = (farmId, params = {}) =>
    getFarmCoverFields(this.axios, farmId, params);
  getFarmCoverTimeline = (farmId, params = {}) =>
    getFarmCoverTimeline(this.axios, farmId, params);
  getFarmCoverProjectionTimeline = (farmId, params) =>
    getFarmCoverProjectionTimeline(this.axios, farmId, params);
  getFarmCoverProjection = (farmId, params = {}) =>
    getFarmCoverProjection(this.axios, farmId, params);
  getFarmGrowthLeague = (farmId, params = {}) =>
    getFarmGrowthLeague(this.axios, farmId, params);
  postFarm = async (obj) => {
    const result = await postFarm(this.axios, obj);
    this.dispatchEvent("postFarm", result);
    return result;
  };
  patchFarm = (id, obj) => patchFarm(this.axios, id, obj);
  exportFarmEvents = (id, fieldIds) =>
    exportFarmEvents(this.axios, id, fieldIds);
  getExportableFarmEventFields = (id) =>
    getExportableFarmEventFields(this.axios, id);
  getClearSinceFields = (id) => getClearSinceFields(this.axios, id);
  previewClearSinceEvents = (id, payload) =>
    previewClearSinceEvents(this.axios, id, payload);
  clearSinceEvents = (id, payload) => clearSinceEvents(this.axios, id, payload);
  deleteFarm = (id) => deleteFarm(this.axios, id);

  getField = (id) => getField(this.axios, id);
  getFieldDashboard = (fieldId, params = {}) =>
    getFieldDashboard(this.axios, fieldId, params);
  getFieldDashboardForecast = (fieldId) =>
    getFieldDashboardForecast(this.axios, fieldId);
  getFields = (filter = {}) => getFields(this.axios, filter);
  lookupAgribotField = ({ lat, lng }) =>
    lookupAgribotField(this.axios, { lat, lng });
  postField = (obj) => postField(this.axios, obj);
  putField = (id, obj) => putField(this.axios, id, obj);
  patchField = (id, obj) => patchField(this.axios, id, obj);
  deleteField = (id) => deleteField(this.axios, id);

  getRegionalForecastPredictions = () =>
    getRegionalForecastPredictions(this.axios);

  // Reports
  getDaysSinceReport = (farmId) => getDaysSinceReport(this.axios, farmId);

  // Notes (farm-level journal + field notes)
  getNotes = (params = {}) => getNotes(this.axios, params);
  postNote = (obj) => postNote(this.axios, obj);
  patchNote = (id, obj) => patchNote(this.axios, id, obj);
  deleteNote = (id) => deleteNote(this.axios, id);

  // Invitations & members
  validateInvitation = (token) => validateInvitation(this.axios, token);
  acceptInvitation = async (token, email) => {
    const result = await acceptInvitation(this.axios, token, email);
    this.dispatchEvent("postFarm", result);
    return result;
  };
  getMembers = (farmId) => getMembers(this.axios, farmId);
  getMemberDisplayNames = (subs) => getMemberDisplayNames(this.axios, subs);
  inviteMember = (farmId, payload) => inviteMember(this.axios, farmId, payload);
  revokeInvitation = (inviteId) => revokeInvitation(this.axios, inviteId);
  regenerateInviteToken = (inviteId) =>
    regenerateInviteToken(this.axios, inviteId);
  removeMember = (accessId) => removeMember(this.axios, accessId);
  getMyInvitations = () => getMyInvitations(this.axios);
  acceptInvitationById = async (inviteId, email) => {
    const result = await acceptInvitationById(this.axios, inviteId, email);
    this.dispatchEvent("postFarm", result);
    return result;
  };
  declineInvitation = (inviteId) => declineInvitation(this.axios, inviteId);

  // User
  getCurrentUser = () => getCurrentUser(this.axios);
  warmForecast = () => postForecastWarmup(this.axios);
  updateProfile = (payload) => updateProfile(this.axios, payload);

  // Admin - Invitations
  adminGetInvitations = (params) => adminGetInvitations(this.axios, params);
  adminGetInvitationStats = () => adminGetInvitationStats(this.axios);
  adminCreateInvitation = (payload) =>
    adminCreateInvitation(this.axios, payload);
  adminRevokeInvitation = (inviteId) =>
    adminRevokeInvitation(this.axios, inviteId);

  // Admin - Users
  adminGetUsers = (params) => adminGetUsers(this.axios, params);

  // Admin - Farms
  adminGetFarms = (params) => adminGetFarms(this.axios, params);
  adminExportFarmEvents = (farmId, fieldIds) =>
    adminExportFarmEvents(this.axios, farmId, fieldIds);
  adminGetExportableFarmEventFields = (farmId) =>
    adminGetExportableFarmEventFields(this.axios, farmId);
  adminGetClearSinceFields = (farmId) =>
    adminGetClearSinceFields(this.axios, farmId);
  adminPreviewClearSinceEvents = (farmId, payload) =>
    adminPreviewClearSinceEvents(this.axios, farmId, payload);
  adminClearSinceEvents = (farmId, payload) =>
    adminClearSinceEvents(this.axios, farmId, payload);

  // Admin - Fields
  adminGetFields = (params) => adminGetFields(this.axios, params);
  adminExportFieldShapes = (farmIds) =>
    adminExportFieldShapes(this.axios, farmIds);

  // Admin - Events
  adminGetEventCoverMeasures = (params) =>
    adminGetEventCoverMeasures(this.axios, params);
  adminGetEventFertilisers = (params) =>
    adminGetEventFertilisers(this.axios, params);

  // Admin - Prediction logs
  adminGetPredictionLogs = (params) =>
    adminGetPredictionLogs(this.axios, params);

  // Forecast analysis (internal allowlist, not admin-only)
  getForecastAnalysisModels = () => getForecastAnalysisModels(this.axios);
  getForecastAnalysisFarms = (params = {}) =>
    getForecastAnalysisFarms(this.axios, params);
  getForecastAnalysisFields = (params = {}) =>
    getForecastAnalysisFields(this.axios, params);
  getForecastAnalysisHistory = (fieldId, params = {}) =>
    getForecastAnalysisHistory(this.axios, fieldId, params);
  getForecastAnalysisCalendar = (fieldId, params = {}) =>
    getForecastAnalysisCalendar(this.axios, fieldId, params);
  postForecastAnalysisHindcast = (fieldId, payload) =>
    postForecastAnalysisHindcast(this.axios, fieldId, payload);
  importSatelliteDetections = (csvText) =>
    importSatelliteDetections(this.axios, csvText);
  getSatelliteDetectionHistory = (fieldId, params = {}) =>
    getSatelliteDetectionHistory(this.axios, fieldId, params);

  // Admin - Farm Access
  adminGetFarmAccess = (params) => adminGetFarmAccess(this.axios, params);
  adminCreateFarmAccess = (payload) =>
    adminCreateFarmAccess(this.axios, payload);

  // Weather
  getWeatherForecast = ({ lat, lon, countryCode = "GB" }) =>
    getWeatherForecast(this.axios, { lat, lon, countryCode });
}

async function del(axios, type, id) {
  const { data } = await axios.delete(`/${type}/${id}`);
  return data;
}

async function post(axios, type, obj) {
  const { data } = await axios.post(`/${type}`, obj);
  return data;
}

async function postBlob(axios, type, obj) {
  const response = await axios.post(`/${type}`, obj, { responseType: "blob" });
  return response;
}

async function get(axios, type, options = {}) {
  const { data } = await axios.get(`/${type}`, options);
  return data;
}

async function put(axios, type, id, obj) {
  const { data } = await axios.put(`/${type}/${id}`, obj);
  return data;
}

async function patch(axios, type, id, obj) {
  const { data } = await axios.patch(`/${type}/${id}`, obj);
  return data;
}

async function patchNoId(axios, type, obj) {
  const { data } = await axios.patch(`/${type}`, obj);
  return data;
}

const getWpClass = (axios, date) => get(axios, `circulation/${date}`);
const getRegionalForecastPredictions = (axios) =>
  get(axios, "circulation/regionalForecastPredictions");
const getMapConfig = (axios) => get(axios, "config/maps");

const getEventCoverMeasures = (axios, filter = {}) =>
  get(axios, "eventcovermeasure", { params: filter });
const getEventCoverMeasureChart = (axios, filter = {}) =>
  get(axios, "eventcovermeasure/chart", { params: filter });
const getEventCoverMeasure = (axios, id) =>
  get(axios, `eventcovermeasure/${id}`);
const postEventCoverMeasure = (axios, obj) =>
  post(axios, "eventcovermeasure", obj);
const patchEventCoverMeasure = (axios, id, obj) =>
  patch(axios, "eventcovermeasure", id, obj);
const deleteEventCoverMeasure = (axios, id) =>
  del(axios, "eventcovermeasure", id);

const getEventFertilisers = (axios, filter = {}) =>
  get(axios, "eventfertiliser", { params: filter });
const getEventFertiliserChart = (axios, filter = {}) =>
  get(axios, "eventfertiliser/chart", { params: filter });
const getEventFertiliser = (axios, id) => get(axios, `eventfertiliser/${id}`);
const postEventFertiliser = (axios, obj) => post(axios, "eventfertiliser", obj);
const patchEventFertiliser = (axios, id, obj) =>
  patch(axios, "eventfertiliser", id, obj);
const deleteEventFertiliser = (axios, id) => del(axios, "eventfertiliser", id);
const postEventCoverMeasuure = (axios, obj) =>
  post(axios, "eventcovermeasure", obj);

const getEventGrazings = (axios, filter = {}) =>
  get(axios, "eventgrazing", { params: filter });
const getEventGrazing = (axios, id) => get(axios, `eventgrazing/${id}`);
const postEventGrazing = (axios, obj) => post(axios, "eventgrazing", obj);
const patchEventGrazing = (axios, id, obj) =>
  patch(axios, "eventgrazing", id, obj);
const deleteEventGrazing = (axios, id) => del(axios, "eventgrazing", id);

const getEventCuts = (axios, filter = {}) =>
  get(axios, "eventcut", { params: filter });
const getEventCut = (axios, id) => get(axios, `eventcut/${id}`);
const postEventCut = (axios, obj) => post(axios, "eventcut", obj);
const patchEventCut = (axios, id, obj) => patch(axios, "eventcut", id, obj);
const deleteEventCut = (axios, id) => del(axios, "eventcut", id);

const getEventReseedings = (axios, filter = {}) =>
  get(axios, "eventreseeding", { params: filter });
const getEventReseeding = (axios, id) => get(axios, `eventreseeding/${id}`);
const postEventReseeding = (axios, obj) => post(axios, "eventreseeding", obj);
const patchEventReseeding = (axios, id, obj) =>
  patch(axios, "eventreseeding", id, obj);
const deleteEventReseeding = (axios, id) => del(axios, "eventreseeding", id);

const getNotes = (axios, params = {}) => get(axios, "note", { params });
const postNote = (axios, obj) => post(axios, "note", obj);
const patchNote = (axios, id, obj) => patch(axios, "note", id, obj);
const deleteNote = (axios, id) => del(axios, "note", id);

const getFarms = (axios) => get(axios, "farm");
const getFarmCoverSummary = (axios, farmId) =>
  get(axios, `farm/${farmId}/cover/summary`);
/**
 * Request the farm's current cover distribution through the authenticated client.
 * @param {object} axios - Configured Axios instance.
 * @param {string} farmId - Farm UUID.
 * @returns {Promise<object>} Parsed API distribution response.
 */
const getFarmCoverDistribution = (axios, farmId) =>
  get(axios, `farm/${farmId}/cover/distribution`);
const getFarmCoverFields = (axios, farmId, params = {}) =>
  get(axios, `farm/${farmId}/cover/fields`, { params });
const getFarmCoverTimeline = (axios, farmId, params = {}) =>
  get(axios, `farm/${farmId}/cover/timeline`, { params });
const getFarmCoverProjectionTimeline = (axios, farmId, params) =>
  get(axios, "farm/" + farmId + "/cover/projection/timeline", { params });
const getFarmCoverProjection = (axios, farmId, params = {}) =>
  get(axios, "farm/" + farmId + "/cover/projection", { params });
const getFarmGrowthLeague = (axios, farmId, params = {}) =>
  get(axios, `farm/${farmId}/cover/growth-league`, { params });
const postFarm = (axios, obj) => post(axios, "farm", obj);
const patchFarm = (axios, id, obj) => patch(axios, "farm", id, obj);
const deleteFarm = (axios, id) => del(axios, "farm", id);
const csvExport = async (axios, endpoint, fieldIds) => {
  const response = await postBlob(axios, endpoint, { fieldIds });
  const filename =
    (response.headers?.["content-disposition"] || "").match(
      /filename="([^"]+)"/
    )?.[1] || "agribot-events.csv";
  return {
    blob: response.data,
    filename,
    skippedEvents: Number(
      response.headers?.["x-agribot-export-skipped-events"] || 0
    ),
  };
};
const exportFarmEvents = (axios, id, fieldIds) =>
  csvExport(axios, `farm/${id}/events/export`, fieldIds);
const getExportableFarmEventFields = (axios, id) =>
  get(axios, `farm/${id}/events/export-fields`);
const getClearSinceFields = (axios, id) =>
  get(axios, `farm/${id}/events/clear-since/fields`);
const previewClearSinceEvents = (axios, id, payload) =>
  post(axios, `farm/${id}/events/clear-since/preview`, payload);
const clearSinceEvents = (axios, id, payload) =>
  post(axios, `farm/${id}/events/clear-since`, payload);

const getField = (axios, id) => get(axios, `field/${id}`);
const getFieldDashboard = (axios, fieldId, params = {}) =>
  get(axios, `field/${fieldId}/dashboard`, { params });
const getFieldDashboardForecast = (axios, fieldId) =>
  get(axios, `field/${fieldId}/dashboard-forecast`);
const getFields = (axios, filter = {}) =>
  get(axios, "field", { params: { q: JSON.stringify(filter) } });
const getDaysSinceReport = (axios, farmId) =>
  get(axios, "report/days-since", { params: { farm: farmId } });
const lookupAgribotField = (axios, { lat, lng }) =>
  get(axios, "field/agribot-lookup", { params: { lat, lng } });
const postField = (axios, obj) => post(axios, "field", obj);
const putField = (axios, id, obj) => put(axios, "field", id, obj);
const patchField = (axios, id, obj) => patch(axios, "field", id, obj);
const deleteField = (axios, id) => del(axios, "field", id);

// Invitation helpers
const validateInvitation = (axios, token) =>
  get(axios, "invitation/validate", { params: { token } });

const acceptInvitation = (axios, token, email) =>
  post(axios, "invitation/accept", { token, email });

const getMembers = (axios, farmId) =>
  get(axios, `invitation/farm/${farmId}/members`);

const getMemberDisplayNames = (axios, subs) =>
  get(axios, "member-display-names", { params: { subs: subs.join(",") } });

const inviteMember = (axios, farmId, payload) =>
  post(axios, `invitation/farm/${farmId}/invite`, payload);

const revokeInvitation = (axios, inviteId) =>
  post(axios, `invitation/${inviteId}/revoke`, {});

const regenerateInviteToken = (axios, inviteId) =>
  post(axios, `invitation/${inviteId}/regenerate`, {});

const removeMember = (axios, accessId) =>
  post(axios, `invitation/member/${accessId}/remove`, {});

const getMyInvitations = (axios) => get(axios, "invitation/my-invitations");

const acceptInvitationById = (axios, inviteId, email) =>
  post(axios, `invitation/${inviteId}/accept-by-id`, { email });

const declineInvitation = (axios, inviteId) =>
  post(axios, `invitation/${inviteId}/decline`, {});

// User helpers
const getCurrentUser = (axios) => get(axios, "user/me");
const postForecastWarmup = (axios) => axios.post("user/forecast-warmup");
const updateProfile = (axios, payload) =>
  patchNoId(axios, "user/profile", payload);

// Admin helpers
const adminGetInvitations = (axios, params = {}) =>
  get(axios, "admin/invitations", { params });
const adminGetInvitationStats = (axios) =>
  get(axios, "admin/invitations/stats");
const adminCreateInvitation = (axios, payload) =>
  post(axios, "admin/invitations", payload);
const adminRevokeInvitation = (axios, inviteId) =>
  post(axios, `admin/invitations/${inviteId}/revoke`, {});
const adminGetUsers = (axios, params = {}) =>
  get(axios, "admin/users", { params });
const adminGetFarms = (axios, params = {}) =>
  get(axios, "admin/farms", { params });
const adminExportFarmEvents = (axios, farmId, fieldIds) =>
  csvExport(axios, `admin/farms/${farmId}/events/export`, fieldIds);
const adminGetExportableFarmEventFields = (axios, farmId) =>
  get(axios, `admin/farms/${farmId}/events/export-fields`);
const adminGetClearSinceFields = (axios, farmId) =>
  get(axios, `admin/farms/${farmId}/events/clear-since/fields`);
const adminPreviewClearSinceEvents = (axios, farmId, payload) =>
  post(axios, `admin/farms/${farmId}/events/clear-since/preview`, payload);
const adminClearSinceEvents = (axios, farmId, payload) =>
  post(axios, `admin/farms/${farmId}/events/clear-since`, payload);
const adminGetFields = (axios, params = {}) =>
  get(axios, "admin/fields", { params });
const adminExportFieldShapes = async (axios, farmIds) => {
  const response = await postBlob(axios, "admin/fields/export", { farmIds });
  const disposition = response.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename="([^"]+)"/);
  return {
    blob: response.data,
    filename: match?.[1] || "field-shapes.geojson",
  };
};
const adminGetEventCoverMeasures = (axios, params = {}) =>
  get(axios, "admin/eventcovermeasures", { params });
const adminGetEventFertilisers = (axios, params = {}) =>
  get(axios, "admin/eventfertilisers", { params });
const adminGetPredictionLogs = (axios, params = {}) =>
  get(axios, "admin/prediction-logs", { params });
const adminGetFarmAccess = (axios, params = {}) =>
  get(axios, "admin/farmaccess", { params });
const adminCreateFarmAccess = (axios, payload) =>
  post(axios, "admin/farmaccess", payload);

// Forecast analysis helpers. These use the same authenticated instance as every other
// browser call — never the admin routes and never the M2M-only /internal namespace.
const getForecastAnalysisModels = (axios) =>
  axios.get("/forecast-analysis/models").then((response) => response.data);
const getForecastAnalysisFarms = (axios, params = {}) =>
  get(axios, "forecast-analysis/farms", { params });
const getForecastAnalysisFields = (axios, params = {}) =>
  get(axios, "forecast-analysis/fields", { params });
const getForecastAnalysisHistory = (axios, fieldId, params = {}) =>
  get(axios, `forecast-analysis/fields/${fieldId}/history`, { params });
const getForecastAnalysisCalendar = (axios, fieldId, params = {}) =>
  get(axios, `forecast-analysis/fields/${fieldId}/calendar`, { params });
const postForecastAnalysisHindcast = (axios, fieldId, payload) =>
  post(axios, `forecast-analysis/fields/${fieldId}/hindcast`, payload);
const importSatelliteDetections = (axios, csvText) =>
  axios
    .post("/forecast-analysis/satellite-detections/imports", csvText, {
      headers: { "Content-Type": "text/csv" },
    })
    .then(({ data }) => data);
const getSatelliteDetectionHistory = (axios, fieldId, params = {}) =>
  get(
    axios,
    `forecast-analysis/satellite-detections/fields/${fieldId}/history`,
    { params }
  );

// Weather helpers
const getWeatherForecast = (axios, { lat, lon, countryCode }) =>
  post(axios, "weather/forecast", {
    location: { type: "point", lat, lon, countryCode },
    time: { scope: "all", granularity: "daily" },
    source: { preferredProviders: ["metoffice"] },
  });

export default GrassAPI;
